import type { components } from "@/lib/api/types.gen";

type Notification = components["schemas"]["Notification"];

export interface ActivityItem {
  id: string;
  type: "grade" | "discussion" | "deadline" | "endorsed";
  text: string;
  strongText: string;
  time: string;
  externalUrl?: string;
  internalPath?: string;
}

type TranslateFn = (key: string, values?: Record<string, string>) => string;

/**
 * Map a notification to a display-ready activity item.
 * Shared by DashboardPage and CourseActivityPanel.
 */
export function mapNotificationToActivity(
  n: Notification,
  t: TranslateFn
): ActivityItem {
  let activityType: ActivityItem["type"] = "deadline";
  if (n.type === "grade_published") activityType = "grade";
  else if (n.type === "discussion_reply") activityType = "discussion";
  else if (n.type === "endorsed_answer") activityType = "endorsed";

  const created = new Date(n.created_at);
  const diffMs = Date.now() - created.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const timeStr =
    diffHours < 1
      ? t("time.justNow")
      : diffHours < 24
        ? t("time.hoursAgo", { count: String(diffHours) })
        : t("time.daysAgo", { count: String(Math.floor(diffHours / 24)) });

  // Classify action_url as internal path or external URL based on type and format
  let internalPath: string | undefined;
  let externalUrl: string | undefined;

  if (activityType === "grade") {
    // Grade notifications always point to internal course pages
    internalPath = n.action_url;
  } else if (n.type === "deadline_reminder") {
    // Deadline activities navigate to /deadlines page per D-01
    internalPath = "/deadlines";
  } else if (n.action_url?.startsWith("/")) {
    // Internal route (discussion/endorsed with local path, or other types)
    internalPath = n.action_url;
  } else if (n.action_url?.startsWith("http")) {
    // External URL (Ed Discussion/Lessons links)
    externalUrl = n.action_url;
  }

  return {
    id: n.id,
    type: activityType,
    text: n.body,
    strongText: n.title.split(":")[0].split(" ").slice(-2).join(" "),
    time: timeStr,
    externalUrl,
    internalPath,
  };
}
